import { useEffect } from "react";
import { signal } from "@preact/signals-react";

import mainStore from "store/mainStore";
import proxy from "api/proxy";
import dates from "tools/dates";
import func from "tools/functions";
import SelectionCardField from "components/MeForm/SelectionCardField";
import StdButton from "components/Buttons/StdButton";
import CharInlineField from "components/MeForm/CharInlineField";
import CheckboxField from "components/MeForm/CheckboxField";
import { Alert } from "components/Tools/Alert";

const ATTRS = { level: "wizard" };

// Fields Configuration
const fields = {
  statement: {
    name: "statement",
    label: "account.statement.statement",
    sizeLabel: "text-3xl mb-6",
  },
  amount: {
    name: "amount",
    label: "account.statement.amount",
    type: "number",
    specialCharacters: true,
  },
  voucher: {
    name: "voucher",
    label: "account.statement.voucher",
    type: "char",
  },
  otherIncome: {
    name: "other_income",
    label: "account.statement.other_income",
    type: "boolean",
  },
};

const state = {
  selectedCard: signal(null),
  payMethods: signal([]),
  amount: signal(0),
  voucher: signal(""),
  loading: signal(false),
  otherIncome: signal(false),
  residual_amount: signal(0),
  isLocked: signal(false),
  showAlert: signal({ status: false, text: null, color: "", icon: "" }),
};

function WizardAddPayment({ onClose, parentRec }) {
  const { record } = mainStore();
  const _terminal = func.getCookie();
  console.log(parentRec, "validate props");

  useEffect(() => {
    async function initData() {
      console.log("init data");
      state.selectedCard.value = null;
      state.voucher.value = "";
      state.amount.value = 0;
      state.residual_amount.value = 0;
      state.otherIncome.value = false;
      state.isLocked.value = false;
      state.showAlert.value = { status: false, text: null };

      const { data } = await proxy.browse(
        "sale.sale",
        [record.id],
        ["residual_amount", "payments", "other_payments", "total_amount"],
      );
      console.log(data, "this is invoice");
      if (data?.[0]) {
        const sale = data[0];
        state.amount.value = sale.residual_amount;
        state.residual_amount.value = sale.residual_amount;

        const hasStandard = sale.payments?.length > 0;
        const hasOther = sale.other_payments?.length > 0;

        state.isLocked.value = hasStandard || hasOther;
        state.otherIncome.value = hasOther;
      }

      await fetchPayMethods();
      return () => {
        state.selectedCard.value = null;
        state.voucher.value = "";
        state.amount.value = 0;
        state.residual_amount.value = 0;
        state.otherIncome.value = false;
        state.isLocked.value = false;
        state.showAlert.value = { status: false, text: null };
        console.log("unmount component");
      };
    }

    async function fetchPayMethods() {
      const { data } = await proxy.search(
        "account.statement",
        [
          ["sale_device", "=", Number(_terminal.device_id)],
          ["state", "=", "draft"],
        ],
        [
          "id",
          "rec_name",
          "journal.name",
          "journal.require_voucher",
          "journal.kind",
        ],
      );
      if (data?.length) {
        state.payMethods.value = data.map((item) => ({
          ...item,
          rec_name: item["journal."]?.name,
        }));
      }
    }

    initData();
  }, [parentRec.id]);

  const notify = (text, type = "error") => {
    state.showAlert.value = {
      status: true,
      text,
      icon: type === "error" ? "xmark" : "check",
      color: type === "success" ? "bg-green-300" : undefined,
    };
  };

  async function handlePayment() {
    const { amount, otherIncome, selectedCard, voucher, residual_amount } =
      state;

    if (!amount.value || amount.value <= 0)
      return notify("Monto debe ser mayor a 0");
    if (amount.value > residual_amount.value + 0.01)
      return notify(`Monto excede saldo pendiente ${residual_amount.value}`);

    if (!otherIncome.value) {
      const requireVoucher = selectedCard.value?.["journal."]?.require_voucher;
      if (!selectedCard.value) return notify("Seleccione método de pago");
      if (requireVoucher && !voucher.value)
        return notify("Voucher es requerido");
    }

    state.loading.value = true;
    try {
      const success = otherIncome.value
        ? await processAdditionalIncome()
        : await processStandardPayment();

      if (success) {
        notify("Pago registrado correctamente", "success");
        setTimeout(async () => {
          const { data } = await proxy.browse(
            "sale.sale",
            [record.id],
            ["residual_amount"],
          );
          if (data?.[0]?.residual_amount <= 0.01) {
            handleClose(true);
          } else {
            const newResidual = data[0].residual_amount;
            state.residual_amount.value = newResidual;
            state.amount.value = newResidual;
            state.voucher.value = "";
            state.selectedCard.value = null;
            state.isLocked.value = true;
          }
        }, 1000);
      }
    } catch (e) {
      console.error(e);
      notify(`Error procesando el pago \n ${e}`);
    } finally {
      state.loading.value = false;
    }
  }

  async function processAdditionalIncome() {
    const cashMethod = state.payMethods.value.find(
      (s) => s["journal."].kind === "cash",
    );
    const { error } = await proxy.create("sale_pos.additional_income", {
      amount: state.amount.value,
      description: record.number,
      party: record.party.id,
      statement: cashMethod,
      origin: `sale.sale,${record.id}`,
    });

    if (!error) {
      const newResidual = state.residual_amount.value - state.amount.value;
      const isDone = newResidual <= 0.01;
      await proxy.saveQuery({
        model: "sale.sale",
        storeRec: {
          id: record.id,
          invoice_type: "M",
          invoice_method: "manual",
          state: isDone ? "done" : "processing",
        },
      });
      return true;
    }
    return false;
  }

  async function processStandardPayment() {
    const toStore = {
      statement: state.selectedCard.value.id,
      amount: String(state.amount.value),
      number: state.voucher.value,
      date: dates.dateToday(),
      description: record.number,
      party: record.party.id,
      account: record.party.account_receivable,
      source: `sale.sale,${record.id}`,
      sale: record.id,
    };

    const { data } = await proxy.create("account.statement.line", toStore, [
      "id",
      "sale.residual_amount",
    ]);
    if (data?.[0]) {
      await proxy.methodInstance({
        model: "account.statement.line",
        method: "create_move",
        instance: data[0].id,
      });
      return true;
    }
    return false;
  }

  function handleClose(update = false) {
    state.voucher.value = null;
    state.selectedCard.value = null;
    state.otherIncome.value = false;
    onClose(update);
  }

  return (
    <>
      <Alert
        show={state.showAlert.value?.status}
        onClose={() => (state.showAlert.value.status = false)}
        text={state.showAlert.value?.text}
        color={state.showAlert.value?.color}
        icon={state.showAlert.value?.icon}
        autoRemove={true}
      />

      <div className="flex flex-col gap-6 p-6 font-sans">
        <div className="flex flex-col md:flex-row gap-4">
          <div
            className={`flex-grow relative group transition-opacity duration-300 ${state.otherIncome.value ? "opacity-40 pointer-events-none" : ""}`}
          >
            {!state.otherIncome.value ? (
              <SelectionCardField
                field={{
                  ...fields.statement,
                  options: state.payMethods.value,
                  label: "Método de Pago",
                }}
                data={state.selectedCard}
                attrs={ATTRS}
              />
            ) : (
              <div className="h-full min-h-[5rem] flex items-center justify-center border-2 border-dashed border-gray-300 rounded-xl text-gray-400 text-sm font-medium bg-gray-50">
                Modo Abono Activo
              </div>
            )}
          </div>

          <div className="md:w-48 flex-shrink-0">
            <div
              className={`h-full p-1 rounded-xl border transition-colors duration-300 ${state.otherIncome.value ? "bg-blue-50 border-blue-200 shadow-sm" : "bg-white border-transparent"} ${state.isLocked.value ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <CheckboxField
                field={{
                  ...fields.otherIncome,
                  label: "Abono / Otro",
                  readOnly: state.isLocked.value,
                }}
                data={state.otherIncome}
                attrs={ATTRS}
                disabled={state.isLocked.value}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          {state.selectedCard.value?.["journal."]?.require_voucher && (
            <div className="mb-4 animate-in fade-in slide-in-from-top-2 duration-300">
              <CharInlineField
                field={fields.voucher}
                data={state.voucher}
                attrs={ATTRS}
              />
            </div>
          )}
          <CharInlineField
            field={{
              ...fields.amount,
              label: state.otherIncome.value
                ? "Monto a Abonar"
                : "Monto a Pagar",
            }}
            data={state.amount}
            attrs={ATTRS}
            autoFocus={true}
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <StdButton
            color="rose"
            onClick={() => handleClose()}
            content="button_cancel"
            variant="text"
            className="hover:bg-rose-50 rounded-lg"
          />
          <StdButton
            onClick={handlePayment}
            content="account.statement.pay"
            loading={state.loading.value}
            size="w-40"
            className="rounded-lg shadow-md hover:shadow-lg transition-shadow"
          />
        </div>
      </div>
    </>
  );
}

export default WizardAddPayment;
